import { useCallback,useState } from 'react';

import { AsyncThunk } from '@reduxjs/toolkit';
import { useAppDispatch } from 'app/hooks';
import { closeDrawer, setNameInConflict } from 'common/components/EditDrawer/editDrawerSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { Operations } from 'common/types/TypeDefs';
import { handleEditDrawerError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

type DrawerOperations = Operations.Edit | Operations.Create | Operations.Link;

interface UseDrawerSubmitOptions<TPayload, TResult> {
    /**
     * The async thunk action to dispatch for submission
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    submitThunk: AsyncThunk<TResult, TPayload, any>;
    /**
     * Entity name for error messages (e.g., 'User Group', 'Role')
     */
    entityName: string;
    /**
     * Success message shown in snackbar
     */
    successMessage: string;
    /**
     * The operation type (Link, Create, Edit)
     */
    operation: DrawerOperations;
    /**
     * Called after successful submission with the result
     */
    onSuccess?: (result: TResult) => void;
    /**
     * Whether to automatically close the drawer on success (default: true)
     */
    closeDrawerOnSuccess?: boolean;
    /**
     * Whether to reset nameInConflict on success (default: true)
     */
    resetNameInConflictOnSuccess?: boolean;
}

interface UseDrawerSubmitReturn<TPayload> {
    /**
     * Whether a submit operation is in progress
     */
    processing: boolean;
    /**
     * Call this to trigger the submit operation
     */
    handleSubmit: (payload: TPayload) => void;
}

/**
 * Hook to handle common drawer submission logic including:
 * - Processing state management
 * - Thunk dispatch and unwrap
 * - Snackbar notifications
 * - Error handling via handleEditDrawerError
 * - Drawer close
 */
export function useDrawerSubmit<TPayload, TResult = unknown>({
    submitThunk,
    entityName,
    successMessage,
    operation,
    onSuccess,
    closeDrawerOnSuccess = true,
    resetNameInConflictOnSuccess = true,
}: UseDrawerSubmitOptions<TPayload, TResult>): UseDrawerSubmitReturn<TPayload> {
    const [processing, setProcessing] = useState(false);
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = useCallback((payload: TPayload) => {
        setProcessing(true);
        dispatch(submitThunk(payload as TPayload & undefined))
            .unwrap()
            .then((result) => {
                enqueueSnackbar(successMessage, { variant: 'success' });
                if (resetNameInConflictOnSuccess) {
                    dispatch(setNameInConflict(false));
                }
                onSuccess?.(result);
                if (closeDrawerOnSuccess) {
                    dispatch(closeDrawer());
                }
            })
            .catch((error: { message: string }) => {
                handleEditDrawerError(
                    operation,
                    extractStatusCode(error.message),
                    entityName,
                    dispatch,
                    enqueueSnackbar
                );
            })
            .finally(() => {
                setProcessing(false);
            });
    }, [submitThunk, entityName, successMessage, operation, onSuccess, closeDrawerOnSuccess, resetNameInConflictOnSuccess, dispatch, enqueueSnackbar]);

    return { processing, handleSubmit };
}
